import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { postsAPI } from '../services/api';
import { Badge } from '../components/ui/badge';
import ReactMarkdown from 'react-markdown';

const PostView = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const data = await postsAPI.getBySlug(slug);
      setPost(data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Post not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <article className="max-w-3xl mx-auto px-6 py-16">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-12"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all articles
        </Link>

        {/* Post header */}
        <header className="mb-12">
          <h1 className="text-5xl font-semibold text-gray-900 mb-6 tracking-tight leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
            <span className="font-medium">{post.author}</span>
            <span>•</span>
            <time dateTime={post.publishedAt}>
              {new Date(post.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </time>
            <span>•</span>
            <span>{post.readingTime} min read</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {post.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="bg-gray-100 text-gray-700">
                {tag}
              </Badge>
            ))}
          </div>
        </header>

        {/* Post content */}
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown
            components={{
              h1: ({node, ...props}) => <h1 className="text-4xl font-semibold text-gray-900 mt-12 mb-6 tracking-tight" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-3xl font-semibold text-gray-900 mt-10 mb-4 tracking-tight" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-3 tracking-tight" {...props} />,
              p: ({node, ...props}) => <p className="text-gray-700 text-lg leading-relaxed mb-6" {...props} />,
              a: ({node, ...props}) => <a className="text-gray-900 underline hover:text-gray-600" {...props} />,
              ul: ({node, ...props}) => <ul className="text-gray-700 text-lg leading-relaxed mb-6 space-y-2" {...props} />,
              ol: ({node, ...props}) => <ol className="text-gray-700 text-lg leading-relaxed mb-6 space-y-2" {...props} />,
              li: ({node, ...props}) => <li className="ml-6" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-6 italic text-gray-600 my-6" {...props} />,
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
};

export default PostView;
